import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter } from 'rxjs';

import { DataspaceSelectionService } from '@core/services/dataspace-selection/dataspace-selection.service';
import { TagsLabelsActions } from '@core/store/admin/tags-labels/tags-labels.actions';
import { CommentsConfigActions } from '@core/store/comments-config/comments-config.actions';
import * as DataspaceActions from '@core/store/dataspace/dataspace.actions';
import * as PlanActions from '@core/store/plan/plan.actions';

/**
 * Service that synchronizes NgRx store state with the current route.
 *
 * This service listens to router navigation events and ensures that context-dependent store
 * state (like dataspace and plan context) is correctly set or cleared based on the current
 * route.
 *
 * ## Problem It Solves
 *
 * Without this service, store state persists across navigation even when the user leaves
 * the context. For example, when navigating from a plan view to the global entities list,
 * the plan ID would remain in the store, causing entities to be incorrectly filtered by the
 * stale plan context.
 *
 * ## Architecture Benefits
 *
 * - **Single source of truth**: Store always reflects current routing context
 * - **Centralized logic**: All route → state synchronization in one place
 * - **Component simplicity**: Components just read from store, no routing logic
 * - **Testability**: Service logic can be unit tested independently
 * - **Consistent pattern**: Same approach for dataspace and plan management
 *
 * @example
 *   // Initialized once during app startup in app.config.ts
 *   provideAppInitializer(() => {
 *     const syncService = inject(RouteStateSyncService);
 *     syncService.initialize();
 *   });
 */
@Injectable({ providedIn: 'root' })
export class RouteStateSyncService {
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly dataspaceSelection = inject(DataspaceSelectionService);

  /**
   * Start synchronizing route state to the store. Call this once during app initialization.
   *
   * This method subscribes to router NavigationEnd events and dispatches appropriate
   * actions to keep the store synchronized with the current route. The subscription lives
   * for the entire application lifecycle.
   *
   * Additionally, it processes the current route immediately to handle direct URL loads,
   * ensuring that plan context is properly loaded even when the user navigates directly to
   * a plan URL.
   */
  initialize(): void {
    // Subscribe to future navigation events
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.syncDataspaceState(event.urlAfterRedirects);
        this.syncPlanState(event.urlAfterRedirects);
      });
  }

  /**
   * Synchronizes dataspace state based on current route.
   *
   * Dispatches fetchDataspace when entering or switching dataspaces, and records the
   * dataspace as the shared selection so admin pages open on it too. Patterns:
   * /ds/:dataspaceId
   * /admin/recipes/new?dataSpaceId=:id and /admin/recipes/:recipeId/edit?dataSpaceId=:id, whose
   * filter preview reads the recipe's dataspace from the store. Opening a recipe is not a pick,
   * so these leave the shared selection alone.
   *
   * @example
   *   // Dataspace routes:
   *   /ds/1/data
   *   /ds/2/planning-spaces
   *
   * @param url - The current URL after redirects
   */
  private syncDataspaceState(url: string): void {
    const match = url.match(/\/ds\/(\d+)/);
    if (match) {
      const dataspaceId = parseInt(match[1], 10);
      this.dataspaceSelection.select(dataspaceId);
      this.loadDataspace(dataspaceId);
      return;
    }

    const recipeForm = url.match(
      /^\/admin\/recipes\/(?:new|\d+\/edit)\?(?:[^#]*&)?dataSpaceId=(\d+)(?:[&#]|$)/
    );
    if (recipeForm) this.loadDataspace(parseInt(recipeForm[1], 10));
    // Note: Don't clear dataspace state - user is always in a dataspace context
  }

  private loadDataspace(dataspaceId: number): void {
    this.store.dispatch(DataspaceActions.fetchDataspace({ id: dataspaceId }));
    this.store.dispatch(TagsLabelsActions.loadLabels({ dataSpaceId: dataspaceId }));
    this.store.dispatch(TagsLabelsActions.loadTags({ dataSpaceId: dataspaceId }));
    this.store.dispatch(
      CommentsConfigActions.loadCommentableTypes({ dataSpaceId: dataspaceId })
    );
  }

  /**
   * Synchronizes plan state based on current route.
   *
   * Dispatches fetchPlans when entering plan routes, clears plan state when leaving.
   *
   * @example
   *   // Plan route (loads state):
   *   /ds/1/planning-spaces/123/plan/456
   *   /ds/1/planning-spaces/123/plan/456/data/LOCATION
   *   /ds/1/planning-spaces/123/plan/456/data/LOCATION/edit/uid-1/form
   *
   *   // Non-plan route (clears state):
   *   /ds/1/data
   *   /ds/1/data/LOCATION
   *   /ds/1/data/LOCATION/edit/uid-1/form
   *   /ds/1/planning-spaces/123
   *
   * @param url - The current URL after redirects
   */
  private syncPlanState(url: string): void {
    // Match URLs containing /planning-spaces/.../plan/:planId (anywhere in the path)
    // This pattern correctly matches both legacy and new harmonized URL structures:
    // - Legacy: /planning-spaces/123/plan/456/LOCATION
    // - New: /planning-spaces/123/plan/456/data/LOCATION
    // - New nested: /planning-spaces/123/plan/456/data/LOCATION/edit/uid/form
    const planRoutePattern = /\/planning-spaces\/[^/]+\/plan\/([^/]+)/;
    const match = url.match(planRoutePattern);

    if (match) {
      const planId = match[1];
      // Load plan when entering plan route
      this.store.dispatch(PlanActions.fetchPlans({ id: planId }));
    } else {
      // Clear plan when leaving plan route
      this.store.dispatch(PlanActions.clearPlan());
    }
  }
}
